/**
 * Freigabeliste der Quellbezeichnungen. Sie stehen im Fehlertext, den Sentry
 * sehen darf (docs/12 «Fehlerprotokolle»); deshalb nur feste Code-Literale,
 * nie Namen, Titel oder andere Daten aus der Abfrage.
 */
export const DIAGNOSTIC_SOURCES = [
  'Anerkennungen', 'Anwesenheitskontext', 'Aufgaben', 'Aufgabenbezug', 'Beobachtungen',
  'Beobachtungsabdeckung', 'Fehlkonzept-Cluster', 'Fehlkonzeptdaten', 'Foerderzyklen',
  'Fördernachprüfungen', 'Förderverlauf', 'Geklärte Fehlkonzepte', 'Klärungen', 'Lernkontrollen',
  'Lernkontrollen: Themenbezug', 'Lernkontrollen: Ticketantworten', 'Lernkontrollen: Ticketfragen',
  'Lernkontrollergebnisse', 'Recall-Antworten', 'Recall-Fehlkonzepte', 'Recall-Verlauf',
  'Reflexionsbezug', 'Sorgfaltsbeobachtungen', 'Sorgfaltsrückmeldungen', 'Stichproben',
  'Stichproben und Textantworten', 'Stichprobenverlauf', 'Tagesrückmeldungen', 'Ticketantworten',
  'Ticketfragen', 'Tickets', 'Verständnisklärungen', 'Wochenpläne', 'Wochenreflexionen',
  'acceptedImpulses', 'filteredTickets', 'hingeCorrectData', 'hingeDiscrepancyData',
  'hingeResponsesData', 'hingeTasksData', 'plansData', 'prevProgress', 'prevTasks', 'questionsData',
  'responsesData', 'sessData', 'spotCheckData', 'studentsData', 'subjectTickets', 'taskSubjects',
  'task_progress', 'task_status_log', 'ticketSubjects',
] as const;
export type DiagnosticSource = typeof DIAGNOSTIC_SOURCES[number];

export const DIAGNOSTIC_READ_ERROR_TYPE = 'DiagnosticReadError';
// PostgREST-Codes und Postgres-SQLSTATE; ein SQLSTATE enthält immer eine Ziffer.
const ERROR_CODE_RE = /^(?:PGRST\d{3}|(?=[0-9A-Z]*\d)[0-9A-Z]{5})$/;

function isDiagnosticSource(value: unknown): value is DiagnosticSource {
  return (DIAGNOSTIC_SOURCES as readonly unknown[]).includes(value);
}

/** 0 heisst bei postgrest-js: keine HTTP-Antwort (Netz- oder Abbruchfehler). */
function isReleasedStatus(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && (value === 0 || (value >= 100 && value <= 599));
}

function isReleasedCode(value: unknown): value is string {
  return typeof value === 'string' && ERROR_CODE_RE.test(value);
}

function describeFailure(source: DiagnosticSource, status: number | undefined, code: string | undefined): string {
  const details = [
    status === undefined ? undefined : status === 0 ? 'keine Antwort' : `HTTP ${status}`,
    code,
  ].filter((part): part is string => part !== undefined);
  return `Diagnosedaten konnten nicht vollständig geladen werden (${source}${details.length ? `; ${details.join(', ')}` : ''}).`;
}

/**
 * Lesefehler einer Diagnosequelle. Der Text besteht nur aus Quellbezeichnung,
 * HTTP-Status und Fehlercode; Meldung, Details und Hinweis der Datenbank
 * bleiben draussen, weil sie Werte aus der Abfrage enthalten können.
 */
export class DiagnosticReadError extends Error {
  readonly source: DiagnosticSource;
  readonly status: number | undefined;
  readonly code: string | undefined;

  constructor(source: DiagnosticSource, status?: number, code?: string) {
    const releasedStatus = isReleasedStatus(status) ? status : undefined;
    const releasedCode = isReleasedCode(code) ? code : undefined;
    super(describeFailure(source, releasedStatus, releasedCode));
    this.name = DIAGNOSTIC_READ_ERROR_TYPE;
    this.source = source;
    this.status = releasedStatus;
    this.code = releasedCode;
  }
}

/**
 * Text für Sentry, falls `error` ein Lesefehler mit freigegebenen Feldern ist.
 * Geprüft über Name und Felder statt `instanceof`, weil Instrumentation und
 * App-Code getrennte Modulkopien laden können. Der Text wird aus den geprüften
 * Feldern neu gebaut; `error.message` wird nie übernommen.
 */
export function releasedDiagnosticReadText(error: unknown): string | undefined {
  if (!(error instanceof Error) || error.name !== DIAGNOSTIC_READ_ERROR_TYPE) return undefined;
  const source = 'source' in error ? error.source : undefined;
  const status = 'status' in error ? error.status : undefined;
  const code = 'code' in error ? error.code : undefined;
  if (!isDiagnosticSource(source)) return undefined;
  if (status !== undefined && !isReleasedStatus(status)) return undefined;
  if (code !== undefined && !isReleasedCode(code)) return undefined;
  return describeFailure(source, isReleasedStatus(status) ? status : undefined, isReleasedCode(code) ? code : undefined);
}

type DiagnosticPage<T> = PromiseLike<{
  data: T[] | null;
  error: { message: string; code?: string } | null;
  status?: number;
}>;

/** Vollständige, stabil sortierte Quellabfragen; Fehler sind niemals leere Daten. */
export async function readDiagnosticRows<T>(
  page: (from: number, to: number) => DiagnosticPage<T>,
  source: DiagnosticSource,
): Promise<T[]> {
  const rows: T[] = [];
  const size = 500;
  for (let offset = 0; ; offset += size) {
    const result = await page(offset, offset + size - 1);
    if (result.error || result.data === null) throw new DiagnosticReadError(source, result.status, result.error?.code);
    rows.push(...result.data);
    if (result.data.length < size) return rows;
  }
}

export async function diagnosticPages<T>(
  page: (from: number, to: number) => DiagnosticPage<T>,
  source: DiagnosticSource,
): Promise<{ data: T[] }> {
  return { data: await readDiagnosticRows(page, source) };
}
